import { Box, Button, Dialog, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    fetchCustomListDetail,
    removeCustomListItems,
} from 'entities/CustomList';
import {
    getSelectedProperty,
    getSelectedPropertyCatalogId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { removeCatalogItemService } from 'features/removeCatalogItem/model/services/removeCatalogItemService';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';
import { Loader } from 'shared/ui/Loader/Loader';

import type { RemoveCustomListItemsMode } from '../RemoveCustomListItemsMenu/RemoveCustomListItemsMenu';

interface RemoveCustomListItemsDialogProps {
    open: boolean;
    mode: RemoveCustomListItemsMode;
    listId: string;
    listName: string;
    /** List-item ids for remove-from-list */
    listItemIds: string[];
    /** Catalog item ids/uuids for remove-from-catalog */
    catalogItemIds: string[];
    onClose: () => void;
    onSuccess?: () => void;
}

export const RemoveCustomListItemsDialog = memo(
    ({
        open,
        mode,
        listId,
        listName,
        listItemIds,
        catalogItemIds,
        onClose,
        onSuccess,
    }: RemoveCustomListItemsDialogProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const catalogId = useSelector(getSelectedPropertyCatalogId);
        const property = useSelector(getSelectedProperty);
        const [loading, setLoading] = useState(false);

        const propertyName =
            property?.propertyNameLegal ||
            property?.propertyNameMarketing ||
            '';

        if (!open) {
            return null;
        }

        const count =
            mode === 'fromList' ? listItemIds.length : catalogItemIds.length;
        const isSingle = count === 1;

        const onSubmit = async () => {
            setLoading(true);

            if (mode === 'fromList') {
                const result = await dispatch(
                    removeCustomListItems({
                        listId,
                        itemIds: listItemIds,
                    }),
                );
                setLoading(false);

                if (result.meta.requestStatus === 'fulfilled') {
                    toast(
                        isSingle
                            ? t('customLists.itemRemovedFromList', {
                                  name: listName,
                              })
                            : t('customLists.itemsRemovedFromList', {
                                  count,
                                  name: listName,
                              }),
                    );
                    onSuccess?.();
                    onClose();
                } else {
                    toast.error(t('customLists.failedToRemoveItems'));
                }
                return;
            }

            if (!catalogId) {
                setLoading(false);
                toast.error(t('customLists.failedToRemoveItems'));
                return;
            }

            const result = await dispatch(
                removeCatalogItemService({
                    catalogId,
                    isSingle,
                    data: {
                        items: catalogItemIds.map((itemUuid) => ({
                            itemUuid,
                        })),
                    },
                }),
            );
            setLoading(false);

            if (result.meta.requestStatus === 'fulfilled') {
                // Catalog delete also removes the item from all lists server-side.
                await dispatch(fetchCustomListDetail({ listId }));
                toast(
                    isSingle
                        ? t('customLists.itemRemovedFromCatalogAndLists')
                        : t('customLists.itemsRemovedFromCatalogAndLists', {
                              count,
                          }),
                );
                onSuccess?.();
                onClose();
            } else {
                toast.error(t('customLists.failedToRemoveItems'));
            }
        };

        const title = isSingle
            ? t('Remove item')
            : t('customLists.removeItemsTitle', { count });

        const boldComponent = <Box component="span" sx={{ fontWeight: 700 }} />;

        const body =
            mode === 'fromList' ? (
                <Trans
                    i18nKey={
                        isSingle
                            ? 'customLists.removeItemFromListConfirm'
                            : 'customLists.removeItemsFromListConfirm'
                    }
                    values={{ name: listName }}
                    components={{ bold: boldComponent }}
                />
            ) : (
                <Trans
                    i18nKey={
                        isSingle
                            ? 'customLists.removeItemFromCatalogConfirm'
                            : 'customLists.removeItemsFromCatalogConfirm'
                    }
                    values={{ catalogName: t('Property Catalog') }}
                    components={{ bold: boldComponent }}
                />
            );

        return (
            <Dialog open onClose={onClose}>
                <DialogBoxContent
                    onClose={onClose}
                    buttons={
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            width="100%"
                            gap="8px"
                        >
                            <Button
                                onClick={onClose}
                                data-testclass="remove-custom-list-items-cancel"
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                color={
                                    mode === 'fromCatalog' ? 'error' : 'primary'
                                }
                                disabled={loading}
                                startIcon={
                                    loading ? (
                                        <Loader color="inherit" />
                                    ) : undefined
                                }
                                onClick={onSubmit}
                                data-testclass="remove-custom-list-items-confirm"
                            >
                                {mode === 'fromList'
                                    ? t('customLists.removeFromList')
                                    : t('customLists.removeFromCatalog')}
                            </Button>
                        </Box>
                    }
                >
                    <Box
                        sx={{
                            maxWidth: '400px',
                            paddingBottom: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                            }}
                        >
                            {propertyName ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'rgba(0, 0, 0, 0.4)',
                                    }}
                                >
                                    {propertyName}
                                </Typography>
                            ) : null}
                            <Typography variant="h4">{title}</Typography>
                        </Box>
                        <Typography variant="body1">{body}</Typography>
                    </Box>
                </DialogBoxContent>
            </Dialog>
        );
    },
);

RemoveCustomListItemsDialog.displayName = 'RemoveCustomListItemsDialog';
